import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useStudio } from "../context/StudioContext";
import Ticker from "../components/Ticker";
import BrandMark from "../components/BrandMark";
import { totalSubServices, SERVICES } from "../lib/services";
import { saveCustomerInfo } from "../lib/tracker";

const COUNTRY_CODES = [
  { dial: "+91", code: "IN" },
  { dial: "+1", code: "US" },
  { dial: "+44", code: "UK" },
  { dial: "+971", code: "AE" },
  { dial: "+61", code: "AU" },
  { dial: "+65", code: "SG" },
  { dial: "+81", code: "JP" },
  { dial: "+49", code: "DE" },
  { dial: "+33", code: "FR" },
  { dial: "+34", code: "ES" },
  { dial: "+55", code: "BR" },
  { dial: "+27", code: "ZA" },
  { dial: "+880", code: "BD" },
  { dial: "+92", code: "PK" },
  { dial: "+94", code: "LK" },
  { dial: "+977", code: "NP" },
];

export default function Landing() {
  const [value, setValue] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("+91");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useStudio();
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = value.trim();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!ok) {
      setError("Enter a valid email to unlock the studio.");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length < 7) {
      setError("Enter a valid mobile number (or leave it blank).");
      return;
    }
    setError("");
    setLoading(true);
    setTimeout(() => {
      signIn(email);
      const fullPhone = phoneDigits ? `${country} ${phoneDigits}` : "";
      saveCustomerInfo({ email, phone: fullPhone, countryCode: country });
      setLoading(false);
      navigate("/dashboard");
    }, 500);
  };

  return (
    <div className="relative min-h-screen bg-brand-ink text-brand-ivory">
      <Ticker />

      <div className="grid min-h-[calc(100vh-44px)] grid-cols-1 lg:grid-cols-[1fr_520px]">
        {/* LEFT — Editorial hero */}
        <section className="relative flex flex-col justify-between px-5 pb-10 pt-10 sm:px-8 sm:pt-12 lg:px-16 lg:pt-16">
          <header className="flex items-center gap-3">
            <BrandMark size={44} />
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-ivory/75">
              AuthorOS &middot; By Zebralearn
            </div>
          </header>

          <div className="max-w-[720px] pt-16 lg:pt-8">
            <div className="mb-8 font-mono text-[11px] uppercase tracking-[0.32em] text-brand-mute">
              Issue №01 &middot; 2026
            </div>
            <h1 className="font-serif-display text-[44px] font-medium leading-[0.95] tracking-tight text-brand-ivory sm:text-[64px] md:text-[86px] lg:text-[108px]">
              Grow{" "}
              <span className="italic text-brand">Your</span>
              <br />
              Book.
            </h1>
            <p className="mt-8 max-w-[540px] text-[15px] leading-relaxed text-brand-ivory/70">
              A production studio for authors, creators, and thinkers. Books, audiobooks, courses & IP — end-to-end.
              No calls, no chase. Pick a service, tell us what you need, we build.
            </p>
          </div>

          <footer className="mt-16 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.28em] text-brand-mute sm:gap-x-10">
            <Stat number={7} label="Services" />
            <div className="hidden h-4 w-px bg-brand-hair sm:block" />
            <Stat number={4} label="Continents" />
            <div className="hidden h-4 w-px bg-brand-hair sm:block" />
            <Stat number={0} label="Meetings" />
            <div className="ml-auto hidden text-brand-mute/70 md:block">
              {totalSubServices()} sub-services
            </div>
          </footer>
        </section>

        {/* RIGHT — Access card */}
        <aside className="relative border-t border-brand-hair bg-brand-panel px-5 py-10 sm:px-8 sm:py-12 lg:border-l lg:border-t-0 lg:px-14 lg:py-16">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-brand" />

          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-mute">
            Access &middot; Step 01
          </div>
          <h2 className="mt-3 font-serif-display text-[36px] font-medium leading-tight tracking-tight text-brand-ivory">
            Enter the Studio.
          </h2>
          <p className="mt-3 max-w-[380px] text-[14px] leading-relaxed text-brand-ivory/60">
            Drop your email to unlock the service dashboard. We use it only to follow up on your inquiry.
          </p>

          <form onSubmit={handleSubmit} className="mt-10">
            <label htmlFor="studio-email" className="block font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Your Email
            </label>
            <input
              id="studio-email"
              type="email"
              autoComplete="email"
              placeholder="name@publisher.com"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-3 w-full border-0 border-b border-brand-hair bg-transparent pb-3 font-serif-display text-[20px] text-brand-ivory placeholder:text-brand-mute/50 focus:border-brand focus:outline-none transition-colors"
            />

            <div className="mt-6 grid grid-cols-[100px_1fr] gap-3 sm:grid-cols-[120px_1fr]">
              <div>
                <label htmlFor="country-code" className="block font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Country
                </label>
                <select
                  id="country-code"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="mt-3 w-full appearance-none border-0 border-b border-brand-hair bg-transparent pb-3 font-serif-display text-[18px] text-brand-ivory focus:border-brand focus:outline-none"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.dial + c.code} value={c.dial} className="bg-brand-panel text-brand-ivory">
                      {c.dial} {c.code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="studio-phone" className="block font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Mobile <span className="text-brand-mute/60">(optional)</span>
                </label>
                <input
                  id="studio-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  placeholder="10-digit number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9\s-]/g, ""))}
                  className="mt-3 w-full border-0 border-b border-brand-hair bg-transparent pb-3 font-serif-display text-[18px] text-brand-ivory placeholder:text-brand-mute/50 focus:border-brand focus:outline-none transition-colors"
                />
              </div>
            </div>
            {error && (
              <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-brand">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group mt-8 flex w-full items-center justify-center gap-3 bg-brand px-6 py-4 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-all hover:bg-[#ff5c26] active:translate-y-[1px] disabled:opacity-60"
            >
              {loading ? "Unlocking..." : "Enter Studio"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </form>

          <div className="mt-6 flex items-start gap-2 text-[12px] leading-relaxed text-brand-ivory/50">
            <ShieldCheck className="mt-[2px] h-4 w-4 flex-none text-brand-ivory/40" />
            <p>
              We never share your email. By continuing you agree to be contacted regarding your inquiries.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-serif-display text-[18px] text-brand-ivory">
        {String(number).padStart(2, "0")}
      </span>
      <span>{label}</span>
    </div>
  );
}
